import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React__default, { createElement } from 'react';
import { motion } from 'framer-motion';
import { styled, css } from 'styled-components';
import { isInMobile, useAuthStatus } from '@fabric-app/compat';
import { useQueryResourceDetail } from '../../../../resources/queries/useQueryResourceDetail.js';
import { useExpandedFdocContext, ExpandedFdocProvider } from '../../ExpandedFdoc/ExpandedFdocProvider.js';
import { DragHandle, BottomBar, BASE_BOTTOM_BAR_HEIGHT } from './ResizableBottomBar.js';
import { ResourceDataContextProvider } from '../../context/ResourceDataContextProvider.js';
import { ResourceContentSkeleton } from '../Content/Skeleton/ResourceContentSkeleton.js';
import { ModalExpandedResourceTagButton } from '../components/ModalExpandedResourceTagButton.js';
import { ResourceContent } from '../Content/ResourceContent.js';
import { ResourceContentLoadError } from '../Content/LoadError/ResourceContentLoadError.js';
import { ExtraPanelContent } from '../ExtraPanel/ExtraPanelContent.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { useIntersectionObserver } from '@fabric-app/ui/components/IntersectionObserver/useIntersectionObserver';
import { usePanning } from '@fabric-app/ui/hooks/usePanning';
import { SocketIOParentRoomProvider } from '../../../../socket.io/ParentRoom/SocketIOParentRoomProvider.js';
import { isResourceIntegrationItem } from '../../../../resources/utils/resourceDetailGuards.js';
import { ResourceRoomProvider } from '../../../../multiplayer/config/fdocs.config.js';
import { ModalExpandedResourceFloatingBottomContainer } from '../components/ModalExpandedResourceFloatingBottomContainer.js';
import { ModalExpandedResourceMemberBar } from '../components/ModalExpandedResourceMemberBar.js';
import { useExpandedResourceState } from '../../../../resources/stores/useExpandedResourceStore.js';
import { preventForwardPropsConfig } from '@fabric-app/ui/utils/preventForwardProps';
import { useSubDocumentSearch, SubDocumentSearchProvider } from '../../../../search/context/SubDocumentSearchContext.js';
import { useContextShareToken } from '../../../../permission/contexts/AnonShareTokenContext.js';
import { useResourceDataContext } from '../../context/resourceDataContext.js';
import { useResourcePermissionsByCurrentUser } from '../../context/getDisabledActionsReasons.js';
import { useVirtualKeyboardContext } from '../../../../keyboard/keyboard.context.js';
import { AssetsPanel } from '../../AssetsPanel/AssetsPanel.js';
import { Kind } from '@fabric/woody-client';

const Content = styled.div.attrs((props) => {
  const { isEditorFocused, hideBottomBar } = useExpandedFdocContext();
  const { isOpen, isAvailable } = useSubDocumentSearch();
  const { resource } = useResourceDataContext();
  const { permissions } = useResourcePermissionsByCurrentUser(resource);
  const { visualViewport, height } = useVirtualKeyboardContext();
  const occupyFullscreen = (isEditorFocused || isOpen && isAvailable) && !resource?.isReadonly && permissions.resource.edit.allowed || hideBottomBar;
  return {
    ...props,
    occupyFullscreen,
    isInMobileApp: isInMobile(),
    visualHeight: visualViewport?.height,
    visualTop: visualViewport?.offsetTop,
    keyboardHeight: height
  };
}).withConfig(preventForwardPropsConfig([
  "occupyFullscreen",
  "isInMobileApp",
  "visualTop",
  "visualHeight"
])).withConfig({
  displayName: "ResourceViewMobile__Content",
  componentId: "sc-f1ddf80f-0"
})([
  `height:1px;flex-grow:1;background:`,
  `;display:flex;flex-direction:column;overflow:hidden;position:relative;border-radius:0 0 1.5rem 1.5rem;transition:0.35s max-height ease-out;`,
  `{`,
  `}`
], cssVar["color-bg-primary"], mediaMobile, (p) => {
  if (p.occupyFullscreen) {
    if (p.isInMobileApp) {
      return css([
        `min-height:calc(100vh - var(--keyboard-height,0px)) !important;max-height:calc(100vh - var(--keyboard-height,0px)) !important;border-radius:0;`
      ]);
    } else if (p.keyboardHeight) {
      return css([
        `min-height:calc(100dvh - var(--keyboard-height,0px)) !important;max-height:calc(100dvh - var(--keyboard-height,0px)) !important;border-radius:0;`
      ]);
    } else {
      return css([
        `border-radius:0;`
      ]);
    }
  }
});
const Wrapper = styled.div.attrs((p) => {
  const { isEditorFocused, hideBottomBar } = useExpandedFdocContext();
  return {
    ...p,
    "data-is-editor-focused": isEditorFocused || hideBottomBar
  };
}).withConfig({
  displayName: "ResourceViewMobile__Wrapper",
  componentId: "sc-f1ddf80f-1"
})([
  `display:flex;flex-direction:column;flex-grow:1;box-shadow:0 0 10px 0 rgba(`,
  `,0.1);background:black;height:100%;max-height:min(calc(100vh - var(--keyboard-height,0px)),100dvh);`
], cssVar["color-bg-primary-reverse-rgb"]);
const ResourceViewMobile = /* @__PURE__ */ React__default.memo(function ResourceViewUnwrapped({ resourceId, isActive, handleOnDelete }) {
  const { resource, isLoading } = useQueryResourceDetail({
    resourceId,
    includeHidden: true
  }, {
    staleTime: 1e3 * 15
  });
  const containerRef = React__default.useRef(null);
  const isInView = useIntersectionObserver({
    target: containerRef,
    rootMargin: "0px 0px 0px 0px"
  });
  const authStatus = useAuthStatus();
  const isLoggedIn = authStatus === "authenticated";
  const shareToken = useContextShareToken();
  const { expandedResourceSidebarInitTab } = useExpandedResourceState();
  const isContentEnabled = isActive && !!resource && isInView;
  const [bottomAdditionalHeight, setBottomAdditionalHeight] = React__default.useState(expandedResourceSidebarInitTab && isLoggedIn ? 250 : 0);
  const { motionProps, isPanningY } = usePanning({
    panningLayerId: "resource-drag-resize",
    onPan: (_e, info, moreInfo) => {
      if (moreInfo.axis === "y") {
        setBottomAdditionalHeight((prev) => prev - info.delta.y);
      }
    },
    onPanEnd: (_e, { velocity }, { axis }) => {
      if (axis !== "y") {
        return;
      }
      if (velocity.y > 150) {
        setBottomAdditionalHeight(0);
        return;
      }
      if (velocity.y < -150) {
        setBottomAdditionalHeight(window.innerHeight - 100);
        return;
      }
      setBottomAdditionalHeight((prev) => prev < 100 ? 0 : prev);
    }
  });
  const bottomHeight = `calc(${BASE_BOTTOM_BAR_HEIGHT} + ${bottomAdditionalHeight}px)`;
  return /* @__PURE__ */ jsx(ExpandedFdocProvider, {
    handleOnDelete,
    resourceId,
    bottomAdditionalHeight,
    setBottomAdditionalHeight,
    children: /* @__PURE__ */ jsx(SubDocumentSearchProvider, {
      children: /* @__PURE__ */ jsx(ResourceDataContextProvider, {
        resource,
        children: /* @__PURE__ */ jsxs(Wrapper, {
          as: motion.div,
          ref: containerRef,
          children: [
            /* @__PURE__ */ jsx(Content, {
              as: motion.div,
              transition: {
                // for the height change
                duration: 0.08
              },
              ...resource?.kind !== Kind.DOCUMENT ? motionProps : {},
              children: isLoading && !resource ? /* @__PURE__ */ jsx(ResourceContentSkeleton, {}) : resource ? /* @__PURE__ */ jsx(SocketIOParentRoomProvider, {
                stayConnectedWhenDocumentNotVisible: true,
                room: {
                  path: "resources",
                  params: {
                    resourceId
                  }
                },
                enabled: !resource.isDraft && isContentEnabled && shareToken?.type !== "publishing",
                children: /* @__PURE__ */ jsxs(ResourceRoomProvider, {
                  resourceId: resource.id,
                  children: [
                    isContentEnabled && /* @__PURE__ */ jsxs(Fragment, {
                      children: [
                        /* @__PURE__ */ jsxs(ModalExpandedResourceFloatingBottomContainer, {
                          children: [
                            /* @__PURE__ */ jsx(ModalExpandedResourceTagButton, {
                              resourceId
                            }, "tag-button"),
                            !isResourceIntegrationItem(resource) && /* @__PURE__ */ jsx(ModalExpandedResourceMemberBar, {}, "member-bar")
                          ]
                        }),
                        /* @__PURE__ */ jsx(AssetsPanel, {
                          resource
                        })
                      ]
                    }),
                    /* @__PURE__ */ jsx(ResourceContent, {})
                  ]
                })
              }) : /* @__PURE__ */ jsx(ResourceContentLoadError, {})
            }),
            /* @__PURE__ */ jsx(DragHandle, {
              isActive: isPanningY,
              style: {
                pointerEvents: !resource ? "none" : "auto"
              },
              "data-pan-id-ignore-expanded-resource": "y",
              ...motionProps
            }),
            /* @__PURE__ */ createElement(BottomBar, {
              style: {
                height: bottomHeight,
                transition: isPanningY ? "height .08s ease-out" : "height 0.2s ease-out"
              },
              ...motionProps,
              key: resource?.id || resourceId
            }, /* @__PURE__ */ jsx(ExtraPanelContent, {
              resourceId,
              inputsVisible: bottomAdditionalHeight > 190,
              onTabItemSelect: () => {
                if (bottomAdditionalHeight < 480) {
                  setBottomAdditionalHeight(480);
                }
              },
              isContentEnabled
            }))
          ]
        })
      })
    })
  });
});

export { ResourceViewMobile };
//# sourceMappingURL=ResourceViewMobile.js.map
